import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { X, ChevronDown, ChevronUp, Search, Info, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { isFilterSelectAllEnabled } from './filterBarSelectAll';

/** Ayuda en panel: intro + una fila por ítem (mejor que un único `title` largo). */
export interface FilterHelpList {
  intro?: string;
  items: { label: string; text: string }[];
}

export function FilterHelpIconPanel({ list }: { list: FilterHelpList }) {
  const flatAria =
    [list.intro, ...list.items.map((i) => `${i.label}: ${i.text}`)].filter(Boolean).join('. ') ||
    undefined;

  return (
    <div className="group relative inline-flex shrink-0">
      <button
        type="button"
        className="inline-flex cursor-help rounded p-0.5 text-gray-400 outline-none hover:text-blue-600 focus-visible:ring-2 focus-visible:ring-[#1681ba] focus-visible:ring-offset-1"
        aria-label={flatAria}
      >
        <Info className="h-3.5 w-3.5" aria-hidden />
      </button>
      <div
        className="pointer-events-none invisible absolute left-0 z-[60] mt-0.5 w-max max-w-[min(20rem,calc(100vw-2rem))] -translate-y-px pt-1 opacity-0 transition-opacity duration-75 group-hover:pointer-events-auto group-hover:visible group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:visible group-focus-within:opacity-100"
        role="tooltip"
      >
        <div className="rounded-md border border-gray-200 bg-white p-2.5 text-left text-[11px] leading-snug shadow-lg ring-1 ring-black/5">
          {list.intro ? (
            <p className="mb-2 border-b border-gray-100 pb-2 text-gray-700">{list.intro}</p>
          ) : null}
          {list.items.length > 0 ? (
            <ul className="m-0 max-h-52 list-none space-y-2.5 overflow-y-auto p-0">
              {list.items.map((row, idx) => (
                <li key={idx}>
                  <div className="font-semibold text-gray-900">{row.label}</div>
                  <div className="mt-0.5 whitespace-normal text-gray-600">{row.text}</div>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export interface FilterOption {
  id: string;
  name: string;
  options: {
    value: string | number;
    label: string;
  }[];
  /** Si es true, las opciones se cargan con fetchOptionsForFilter al abrir el desplegable (S&OP productos). */
  loadOptionsRemotely?: boolean;
  multiple?: boolean; // Opción para hacer el filtro múltiple (por defecto es único)
  /** En filtros múltiples: permite la fila "Seleccionar todo" (por defecto true). Desactivar en catálogos grandes (p. ej. productos). */
  enableSelectAll?: boolean;
  /** Texto de ayuda (tooltip nativo al pasar sobre el icono ⓘ). */
  helpText?: string;
  /** Panel con lista (prioridad sobre `helpText` si ambos existen). */
  helpList?: FilterHelpList;
  // 
  // Ejemplos de uso:
  // - multiple: true -> Permite seleccionar múltiples opciones del mismo grupo
  // - multiple: false -> Solo permite una selección (reemplaza la anterior)
  // - multiple: undefined -> Por defecto es único (false)
}

interface FilterBarProps {
  filters: FilterOption[];
  onFilterChange: (
    filters: Record<string, string[]>,
    meta?: {
      selectedLabel?: { filterId: string; label: string };
      /** true = todos los ítems del catálogo (sin listar IDs); false = quitar ese modo */
      selectAll?: boolean;
      filterId?: string;
    }
  ) => void;
  className?: string;
  activeFilters?: Array<{ id: string; label: string; value: string }>;
  /** Estado "todos seleccionados" por filtro (p. ej. desde sesión Supply) sin materializar IDs */
  selectAllByFilter?: Record<string, boolean>;
  onClearFilter?: (filterId: string) => void;
  onClearAllFilters?: () => void;
  /** Para filtros con loadOptionsRemotely: búsqueda/slice en servidor (debounce vía debouncedSearchTerms). */
  fetchOptionsForFilter?: (filterId: string, searchQuery: string) => Promise<Array<{ value: string | number; label: string }>>;
  /** Contenido extra dentro del panel expandido (p. ej. rangos de fecha). */
  extraContent?: ReactNode;
  /** Si true, `extraContent` se renderiza en la misma fila/grid que los filtros. */
  extraContentInline?: boolean;
  /** Celdas que aporta `extraContent` cuando va inline (para calcular columnas xl). */
  extraContentColumns?: number;
  /** Pie bajo el grid (p. ej. texto de ayuda). */
  extraFooter?: ReactNode;
}

type FilterOptionItem = { value: string | number; label: string };
type CompactMultiDisplayMode = 'all' | 'exclusions';

function getMultiFilterDisplay(
  filter: FilterOption,
  baseOptions: FilterOptionItem[],
  selectedValues: string[],
  localEntries: Array<{ id: string; label: string; value: string }>,
  compactMode: CompactMultiDisplayMode | undefined,
): {
  mode: 'none' | 'default' | 'all' | 'exclusions' | 'summary';
  includedChips: Array<{ label: string; value: string }>;
  excludedChips: Array<{ label: string; value: string }>;
  summaryCount: number;
} {
  if (filter.multiple !== true) {
    return { mode: 'none', includedChips: [], excludedChips: [], summaryCount: 0 };
  }

  if (compactMode === 'all') {
    return { mode: 'all', includedChips: [], excludedChips: [], summaryCount: 0 };
  }

  if (selectedValues.length === 0) {
    return { mode: 'none', includedChips: [], excludedChips: [], summaryCount: 0 };
  }

  const totalCount = baseOptions.length;
  const allSelected =
    totalCount > 0 &&
    selectedValues.length === totalCount &&
    baseOptions.every((option) => selectedValues.includes(String(option.value)));

  if (allSelected) {
    return { mode: 'all', includedChips: [], excludedChips: [], summaryCount: 0 };
  }

  if (compactMode === 'exclusions') {
    const excludedChips = baseOptions
      .filter((option) => !selectedValues.includes(String(option.value)))
      .map((option) => ({ label: option.label, value: String(option.value) }));
    if (excludedChips.length > 0 && excludedChips.length <= 5) {
      return { mode: 'exclusions', includedChips: [], excludedChips, summaryCount: 0 };
    }
    return {
      mode: 'summary',
      includedChips: [],
      excludedChips: [],
      summaryCount: selectedValues.length,
    };
  }

  const includedChips = localEntries
    .filter((entry) => entry.id === filter.id)
    .map((entry) => ({ label: entry.label, value: entry.value }));

  if (includedChips.length > 5) {
    return {
      mode: 'summary',
      includedChips: [],
      excludedChips: [],
      summaryCount: includedChips.length,
    };
  }

  return { mode: 'default', includedChips, excludedChips: [], summaryCount: 0 };
}

function formatExcludedNames(
  excludedChips: Array<{ label: string; value: string }>,
  maxVisible = 3,
): string {
  if (excludedChips.length === 0) return '';
  const visible = excludedChips.slice(0, maxVisible).map((chip) => chip.label);
  const remaining = excludedChips.length - maxVisible;
  if (remaining > 0) {
    return `${visible.join(', ')} +${remaining}`;
  }
  return visible.join(', ');
}

function getEffectiveSelectedValues(
  filterId: string,
  baseOptions: FilterOptionItem[],
  localEntries: Array<{ id: string; label: string; value: string }>,
  compactMode: CompactMultiDisplayMode | undefined,
): string[] {
  if (compactMode === 'all') {
    return baseOptions.map((option) => String(option.value));
  }
  return localEntries
    .filter((entry) => entry.id === filterId)
    .map((entry) => entry.value);
}

function isFullCatalogSelection(
  filterId: string,
  matchingOptions: FilterOptionItem[],
  baseOptions: FilterOptionItem[],
  debouncedSearchTerms: Record<string, string>,
): boolean {
  const searchTerm = debouncedSearchTerms[filterId] || '';
  return (
    !searchTerm.trim() &&
    matchingOptions.length > 0 &&
    matchingOptions.length === baseOptions.length
  );
}

export function FilterBar({
  filters, 
  onFilterChange, 
  className = '',
  activeFilters = [],
  selectAllByFilter = {},
  onClearFilter,
  onClearAllFilters,
  fetchOptionsForFilter,
  extraContent,
  extraContentInline = false,
  extraContentColumns = 0,
  extraFooter,
}: FilterBarProps) {
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});
  const [localActiveFilters, setLocalActiveFilters] = useState<Array<{ id: string; label: string; value: string }>>(activeFilters);
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const [debouncedSearchTerms, setDebouncedSearchTerms] = useState<Record<string, string>>({});
  const [visibleCounts, setVisibleCounts] = useState<Record<string, number>>({}); // Contador de elementos visibles por filtro
  const [remoteOptions, setRemoteOptions] = useState<Record<string, { value: string | number; label: string }[]>>({});
  const [remoteLoading, setRemoteLoading] = useState<Record<string, boolean>>({});
  const [compactMultiDisplay, setCompactMultiDisplay] = useState<Record<string, CompactMultiDisplayMode>>({});
  const { t } = useTranslation();
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollContainerRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const isUpdatingRef = useRef(false);
  const activeFiltersRef = useRef(activeFilters);
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    activeFiltersRef.current = activeFilters;
  }, [activeFilters]);

  const finishInternalFilterUpdate = useCallback(() => {
    isUpdatingRef.current = false;
    setLocalActiveFilters(activeFiltersRef.current);
  }, []);

  // Sincronizar activeFilters externos con el estado local
  useEffect(() => {
    if (!isUpdatingRef.current) {
      setLocalActiveFilters(activeFilters);
      setCompactMultiDisplay((prev) => {
        const next = { ...prev };

        filters.forEach((filter) => {
          if (filter.multiple !== true) {
            delete next[filter.id];
            return;
          }

          if (selectAllByFilter[filter.id]) {
            next[filter.id] = 'all';
            return;
          }

          const useRemote = Boolean(filter.loadOptionsRemotely && fetchOptionsForFilter);
          const baseOptions = useRemote ? (remoteOptions[filter.id] ?? []) : filter.options;
          if (baseOptions.length === 0) {
            return;
          }

          const selectedValues = activeFilters
            .filter((entry) => entry.id === filter.id)
            .map((entry) => entry.value);
          const allSelected =
            selectedValues.length === baseOptions.length &&
            baseOptions.every((option) => selectedValues.includes(String(option.value)));

          if (selectedValues.length === 0) {
            delete next[filter.id];
          } else if (allSelected) {
            next[filter.id] = 'all';
          } else if (prev[filter.id] === 'all' || prev[filter.id] === 'exclusions') {
            next[filter.id] = 'exclusions';
          }
        });

        return next;
      });
    }
  }, [activeFilters, filters, remoteOptions, fetchOptionsForFilter, selectAllByFilter]);

  // Debounce para términos de búsqueda (optimización de rendimiento)
  useEffect(() => {
    Object.entries(searchTerms).forEach(([filterId, value]) => {
      // Limpiar timer anterior si existe
      if (debounceTimers.current[filterId]) {
        clearTimeout(debounceTimers.current[filterId]);
      }
      
      // Crear nuevo timer con debounce más largo para filtros grandes (como productos)
      const debounceTime = filterId === 'product' ? 300 : 150;
      debounceTimers.current[filterId] = setTimeout(() => {
        setDebouncedSearchTerms(prev => ({
          ...prev,
          [filterId]: value
        }));
        // Resetear el contador de elementos visibles cuando cambia la búsqueda
        setVisibleCounts(prev => ({
          ...prev,
          [filterId]: INITIAL_VISIBLE_OPTIONS
        }));
      }, debounceTime);
    });

    // Cleanup
    return () => {
      Object.values(debounceTimers.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, [searchTerms]);

  // Carga remota de opciones (p. ej. productos S&OP sin listar todo el catálogo al inicio)
  useEffect(() => {
    if (!fetchOptionsForFilter) return;
    const remoteFilters = filters.filter((f) => f.loadOptionsRemotely);
    if (remoteFilters.length === 0) return;

    let cancelled = false;
    void (async () => {
      for (const filter of remoteFilters) {
        if (!openDropdowns[filter.id]) continue;
        setRemoteLoading((prev) => ({ ...prev, [filter.id]: true }));
        try {
          const q = debouncedSearchTerms[filter.id] ?? '';
          const opts = await fetchOptionsForFilter(filter.id, q);
          if (!cancelled) {
            setRemoteOptions((prev) => ({ ...prev, [filter.id]: opts }));
          }
        } catch {
          if (!cancelled) {
            setRemoteOptions((prev) => ({ ...prev, [filter.id]: [] }));
          }
        } finally {
          if (!cancelled) {
            setRemoteLoading((prev) => ({ ...prev, [filter.id]: false }));
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filters, openDropdowns, debouncedSearchTerms, fetchOptionsForFilter]);

  // Cerrar dropdowns cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      Object.entries(openDropdowns).forEach(([filterId, isOpen]) => {
        if (isOpen && dropdownRefs.current[filterId]) {
          const dropdown = dropdownRefs.current[filterId];
          if (dropdown && !dropdown.contains(event.target as Node)) {
            setOpenDropdowns(prev => ({
              ...prev,
              [filterId]: false
            }));
          }
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdowns]);

  // Función para obtener el número de columnas basada en el número de filtros
  const getGridCols = (filterCount: number) => {
    switch (filterCount) {
      case 1:
        return 'lg:grid-cols-1 xl:grid-cols-1';
      case 2:
        return 'lg:grid-cols-2 xl:grid-cols-2';
      case 3:
        return 'lg:grid-cols-3 xl:grid-cols-3';
      case 4:
        return 'lg:grid-cols-4 xl:grid-cols-4';
      case 5:
        return 'lg:grid-cols-5 xl:grid-cols-5';
      case 6:
        return 'lg:grid-cols-6 xl:grid-cols-6';
      default:
        return 'lg:grid-cols-4 xl:grid-cols-4';
    }
  };

  // Constantes para carga incremental
  const INITIAL_VISIBLE_OPTIONS = 50; // Elementos iniciales a mostrar
  const LOAD_MORE_INCREMENT = 50; // Cuántos elementos cargar al hacer scroll
  
  // Función para filtrar opciones basada en el término de búsqueda
  // OPTIMIZACIÓN: Usa debouncedSearchTerms para mejor rendimiento y está memoizada
  // LIMITA el número de resultados para mejorar rendimiento con listas grandes
  
  const getFilteredOptions = useCallback((filterId: string, options: { value: string | number; label: string }[], visibleCount?: number) => {
    const searchTerm = debouncedSearchTerms[filterId] || '';
    const maxVisible = visibleCount || INITIAL_VISIBLE_OPTIONS;
    
    let filtered: { value: string | number; label: string }[];
    
    if (!searchTerm.trim()) {
      // Si no hay búsqueda, limitar a los elementos visibles
      filtered = options.slice(0, maxVisible);
    } else {
      const searchLower = searchTerm.toLowerCase();
      filtered = options.filter(option => 
        option.label.toLowerCase().includes(searchLower)
      ).slice(0, maxVisible);
    }
    
    return filtered;
  }, [debouncedSearchTerms]);
  
  // Función para obtener el total de opciones filtradas (sin límite)
  const getTotalFilteredCount = useCallback((filterId: string, options: { value: string | number; label: string }[]) => {
    const searchTerm = debouncedSearchTerms[filterId] || '';
    
    if (!searchTerm.trim()) {
      return options.length;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return options.filter(option => 
      option.label.toLowerCase().includes(searchLower)
    ).length;
  }, [debouncedSearchTerms]);

  const getAllMatchingOptions = useCallback((filterId: string, options: { value: string | number; label: string }[]) => {
    const searchTerm = debouncedSearchTerms[filterId] || '';

    if (!searchTerm.trim()) {
      return options;
    }

    const searchLower = searchTerm.toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(searchLower));
  }, [debouncedSearchTerms]);

  // Función para manejar cambios en el término de búsqueda
  const handleSearchChange = (filterId: string, value: string) => {
    setSearchTerms(prev => ({
      ...prev,
      [filterId]: value
    }));
  };

  // Función para limpiar búsqueda
  const clearSearch = (filterId: string) => {
    setSearchTerms(prev => ({
      ...prev,
      [filterId]: ''
    }));
  };

  const getBaseOptionsForFilter = useCallback((filterId: string): FilterOptionItem[] => {
    const filter = filters.find((entry) => entry.id === filterId);
    if (!filter) return [];
    const useRemote = Boolean(filter.loadOptionsRemotely && fetchOptionsForFilter);
    return useRemote ? (remoteOptions[filterId] ?? []) : filter.options;
  }, [filters, remoteOptions, fetchOptionsForFilter]);

  const updateCompactDisplay = useCallback((
    filterId: string,
    selectedValues: string[],
    baseOptions: FilterOptionItem[],
    trigger: 'selectAll' | 'deselectAll' | 'toggle' | 'clear',
  ) => {
    setCompactMultiDisplay((prev) => {
      const next = { ...prev };
      const total = baseOptions.length;

      if (trigger === 'selectAll') {
        next[filterId] = 'all';
        return next;
      }

      if (trigger === 'deselectAll' || trigger === 'clear' || selectedValues.length === 0) {
        delete next[filterId];
        return next;
      }

      const allSelected =
        total > 0 &&
        selectedValues.length === total &&
        baseOptions.every((option) => selectedValues.includes(String(option.value)));

      if (allSelected) {
        next[filterId] = 'all';
        return next;
      }

      if (prev[filterId] === 'all' || prev[filterId] === 'exclusions') {
        next[filterId] = 'exclusions';
      }

      return next;
    });
  }, []);

  const handleFilterChange = (filterId: string, value: string) => {
    const filterConfig = filters.find(f => f.id === filterId);
    const baseOptions = getBaseOptionsForFilter(filterId);
    const compactMode = compactMultiDisplay[filterId];
    const currentValues = localActiveFilters
      .filter((f) => f.id === filterId)
      .map((f) => f.value);
    const effectiveSelected = getEffectiveSelectedValues(
      filterId,
      baseOptions,
      localActiveFilters,
      compactMode,
    );
    const isCurrentlySelected = effectiveSelected.includes(value);
    
    // Marcar que estamos actualizando para evitar sincronización
    isUpdatingRef.current = true;
    
    if (isCurrentlySelected) {
      let newValues: string[];
      if (compactMode === 'all') {
        newValues = baseOptions
          .map((option) => String(option.value))
          .filter((id) => id !== value);
      } else {
        newValues = currentValues.filter((v) => v !== value);
      }

      const updatedLocalFilters = localActiveFilters.filter(
        (f) => !(f.id === filterId && f.value === value),
      );
      if (compactMode === 'all') {
        const resolveOption = (optionValue: string) =>
          baseOptions.find((option) => String(option.value) === optionValue);
        const rebuilt = newValues
          .map((optionValue) => {
            const option = resolveOption(optionValue);
            if (!option) return null;
            return {
              id: filterId,
              label: option.label,
              value: optionValue,
            };
          })
          .filter((entry): entry is { id: string; label: string; value: string } => entry !== null);
        setLocalActiveFilters([
          ...localActiveFilters.filter((f) => f.id !== filterId),
          ...rebuilt,
        ]);
      } else {
        setLocalActiveFilters(updatedLocalFilters);
      }
      
      onFilterChange(
        { [filterId]: newValues },
        compactMode === 'all' ? { selectAll: false, filterId } : undefined,
      );
      if (filterConfig?.multiple === true) {
        updateCompactDisplay(
          filterId,
          newValues,
          baseOptions,
          newValues.length === 0 ? 'clear' : 'toggle',
        );
      }
    } else {
      // Si no está seleccionado, determinar si es filtro único o múltiple
      // Usar la configuración del filtro (por defecto es único si no se especifica)
      const isSingleFilter = filterConfig?.multiple !== true; // Por defecto es único
      
      let newValues: string[];
      let updatedLocalFilters: Array<{ id: string; label: string; value: string }>;
      
      if (isSingleFilter) {
        // Filtro único: reemplazar el valor anterior
        newValues = [value];
        
        // Remover el filtro anterior del mismo tipo y agregar el nuevo
        updatedLocalFilters = localActiveFilters.filter(f => f.id !== filterId);
      } else {
        // Filtro múltiple: agregar a la lista existente
        newValues = [...currentValues, value];
        updatedLocalFilters = localActiveFilters;
      }
      
      // Encontrar la opción para obtener el label
      const filterOption = filters.find(f => f.id === filterId);
      const fromStatic = filterOption?.options.find((o) => String(o.value) === value);
      const fromRemote =
        filterOption?.loadOptionsRemotely && fetchOptionsForFilter
          ? remoteOptions[filterId]?.find((o) => String(o.value) === value)
          : undefined;
      const option = fromStatic ?? fromRemote;
      const newLocalFilter = {
        id: filterId,
        label: option?.label ?? value,
        value: String(option?.value ?? value),
      };

      setLocalActiveFilters([...updatedLocalFilters, newLocalFilter]);

      onFilterChange(
        { [filterId]: newValues },
        option ? { selectedLabel: { filterId, label: newLocalFilter.label } } : undefined,
      );
      if (filterConfig?.multiple === true) {
        updateCompactDisplay(
          filterId,
          newValues,
          getBaseOptionsForFilter(filterId),
          'toggle',
        );
      }
    }
    
    setTimeout(() => {
      finishInternalFilterUpdate();
    }, 100);
  };

  const handleSelectAll = (
    filterId: string,
    options: { value: string | number; label: string }[]
  ) => {
    const filterConfig = filters.find((f) => f.id === filterId);
    if (filterConfig?.multiple !== true) return;

    const matchingOptions = getAllMatchingOptions(filterId, options);
    if (matchingOptions.length === 0) return;

    const matchingValues = matchingOptions.map((option) => String(option.value));
    const compactMode = compactMultiDisplay[filterId];
    const currentValues = localActiveFilters
      .filter((f) => f.id === filterId)
      .map((f) => f.value);
    const effectiveSelected = getEffectiveSelectedValues(
      filterId,
      options,
      localActiveFilters,
      compactMode,
    );
    const allMatchingSelected = matchingValues.every((value) =>
      effectiveSelected.includes(value),
    );
    const fullCatalogSelection = isFullCatalogSelection(
      filterId,
      matchingOptions,
      options,
      debouncedSearchTerms,
    );

    isUpdatingRef.current = true;

    if (allMatchingSelected) {
      const newValues = compactMode === 'all'
        ? []
        : currentValues.filter((value) => !matchingValues.includes(value));
      const updatedLocalFilters = compactMode === 'all'
        ? localActiveFilters.filter((f) => f.id !== filterId)
        : localActiveFilters.filter(
            (f) => !(f.id === filterId && matchingValues.includes(f.value)),
          );
      setLocalActiveFilters(updatedLocalFilters);
      onFilterChange(
        { [filterId]: newValues },
        fullCatalogSelection || compactMode === 'all' ? { selectAll: false, filterId } : undefined,
      );
      updateCompactDisplay(
        filterId,
        newValues,
        options,
        newValues.length === 0 ? 'deselectAll' : 'toggle',
      );
    } else if (fullCatalogSelection) {
      setLocalActiveFilters((prev) => prev.filter((f) => f.id !== filterId));
      setCompactMultiDisplay((prev) => ({ ...prev, [filterId]: 'all' }));
      onFilterChange({ [filterId]: [] }, { selectAll: true, filterId });
    } else {
      const newValues = [...new Set([...currentValues, ...matchingValues])];
      const otherFilters = localActiveFilters.filter((f) => f.id !== filterId);
      const resolveOption = (optionValue: string) =>
        options.find((option) => String(option.value) === optionValue);
      const newLocalFilters = [
        ...otherFilters,
        ...newValues
          .map((optionValue) => {
            const option = resolveOption(optionValue);
            if (!option) return null;
            return {
              id: filterId,
              label: option.label,
              value: optionValue,
            };
          })
          .filter((entry): entry is { id: string; label: string; value: string } => entry !== null),
      ];
      setLocalActiveFilters(newLocalFilters);
      onFilterChange({ [filterId]: newValues });
      updateCompactDisplay(filterId, newValues, options, 'toggle');
    }

    setTimeout(() => {
      finishInternalFilterUpdate();
    }, 100);
  };

  const getRemoteFilteredOptions = useCallback(
    (filterId: string, options: FilterOptionItem[]) => {
      const term = (debouncedSearchTerms[filterId] || '').trim().toLowerCase();
      if (!term) return options;
      return options.filter((option) => option.label.toLowerCase().includes(term));
    },
    [debouncedSearchTerms],
  );

  const toggleDropdown = (filterId: string) => {
    const isOpening = !openDropdowns[filterId];
    setOpenDropdowns(prev => ({
      ...prev,
      [filterId]: !prev[filterId]
    }));
    
    // Resetear el contador cuando se abre el dropdown
    if (isOpening) {
      setVisibleCounts(prev => ({
        ...prev,
        [filterId]: INITIAL_VISIBLE_OPTIONS
      }));
    }
  };
  
  // Función para manejar el scroll y cargar más elementos
  const handleScroll = useCallback((filterId: string, options: { value: string | number; label: string }[]) => {
    const container = scrollContainerRefs.current[filterId];
    if (!container) return;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    
    // Si el usuario está cerca del final (80% o más), cargar más elementos
    if (scrollPercentage >= 0.8) {
      setVisibleCounts(prev => {
        const currentCount = prev[filterId] || INITIAL_VISIBLE_OPTIONS;
        const totalFiltered = getTotalFilteredCount(filterId, options);
        
        // Solo cargar más si hay más elementos disponibles
        if (currentCount < totalFiltered) {
          const newCount = Math.min(currentCount + LOAD_MORE_INCREMENT, totalFiltered);
          return {
            ...prev,
            [filterId]: newCount
          };
        }
        return prev;
      });
    }
  }, [getTotalFilteredCount]);

  const clearFilter = (filterId: string) => {
    setCompactMultiDisplay((prev) => {
      const next = { ...prev };
      delete next[filterId];
      return next;
    });
    if (onClearFilter) {
      onClearFilter(filterId);
    }
  };

  const clearAllFilters = () => {
    setCompactMultiDisplay({});
    if (onClearAllFilters) {
      onClearAllFilters();
    }
  };

  const hasActiveFilters =
    localActiveFilters.length > 0 || Object.keys(compactMultiDisplay).length > 0;

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      <div className="flex w-full items-center gap-2 px-4 py-2.5 rounded-t-lg">
        <button
          type="button"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md py-1 pl-2 pr-1 text-left transition-colors hover:bg-gray-50"
          aria-expanded={filtersExpanded}
        >
          <span className="text-sm font-medium text-gray-700">{t('filters.title')}</span>
          <span className="shrink-0 text-gray-400" aria-hidden>
            {filtersExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </span>
        </button>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearAllFilters}
            className="shrink-0 text-xs text-gray-500 hover:text-gray-700"
          >
            {t('filters.clearAll')}
          </button>
        )}
      </div>
      
      <div className={`${filtersExpanded ? 'block' : 'hidden'} px-4 pb-4 pt-3 border-t border-gray-100`}>
        <div
          className={`grid grid-cols-1 md:grid-cols-2 ${getGridCols(
            filters.length + (extraContentInline ? extraContentColumns : 0)
          )} gap-3`}
        >
          {filters.map(filter => {
            const isOpen = openDropdowns[filter.id] || false;
            const useRemote = Boolean(filter.loadOptionsRemotely && fetchOptionsForFilter);
            const baseOptions = useRemote ? (remoteOptions[filter.id] ?? []) : filter.options;
            const compactMode = compactMultiDisplay[filter.id];
            const selectedValuesForFilter = getEffectiveSelectedValues(
              filter.id,
              baseOptions,
              localActiveFilters,
              compactMode,
            );
            const activeCount =
              compactMode === 'all' ? baseOptions.length : selectedValuesForFilter.length;
            
            // Calcular opciones filtradas (las funciones ya están memoizadas con useCallback)
            // No usar useMemo aquí porque estamos dentro de un map
            const currentVisibleCount = visibleCounts[filter.id] || INITIAL_VISIBLE_OPTIONS;
            const remotePool = useRemote
              ? getRemoteFilteredOptions(filter.id, baseOptions)
              : baseOptions;
            const filteredOptions = useRemote
              ? remotePool.slice(0, currentVisibleCount)
              : getFilteredOptions(filter.id, baseOptions, currentVisibleCount);
            const totalFilteredCount = useRemote
              ? remotePool.length
              : getTotalFilteredCount(filter.id, baseOptions);
            const allMatchingOptions = useRemote
              ? remotePool
              : getAllMatchingOptions(filter.id, baseOptions);
            const allMatchingSelected =
              allMatchingOptions.length > 0 &&
              allMatchingOptions.every((option) =>
                selectedValuesForFilter.includes(String(option.value))
              );
            const multiDisplay = getMultiFilterDisplay(
              filter,
              baseOptions,
              selectedValuesForFilter,
              localActiveFilters,
              compactMode,
            );
            // Mostrar mensaje si hay más resultados disponibles que los mostrados
            const hasMoreResults = totalFilteredCount > filteredOptions.length;
            
            return (
              <div key={filter.id} className="space-y-1">
                <div className="flex items-center justify-between gap-1">
                  <div className="flex min-w-0 flex-1 items-center gap-1">
                    <label className="block truncate text-xs font-medium text-gray-600">{filter.name}</label>
                    {filter.helpList && (filter.helpList.intro || filter.helpList.items.length > 0) ? (
                      <FilterHelpIconPanel list={filter.helpList} />
                    ) : filter.helpText ? (
                      <span
                        className="inline-flex shrink-0 cursor-help text-gray-400 hover:text-blue-600"
                        title={filter.helpText}
                        aria-label={filter.helpText}
                      >
                        <Info className="h-3.5 w-3.5" aria-hidden />
                      </span>
                    ) : null}
                  </div>
                  {activeCount > 0 && (
                    <button
                      type="button"
                      onClick={() => clearFilter(filter.id)}
                      className="text-[11px] text-gray-500 hover:text-gray-700"
                    >
                      {t('filters.clear')}
                    </button>
                  )}
                </div>
                
                {/* Dropdown button */}
                <div className="relative" ref={(el) => { dropdownRefs.current[filter.id] = el; }}>
                  <button
                    type="button"
                    onClick={() => toggleDropdown(filter.id)}
                    className={`mt-0.5 block w-full pl-2.5 pr-8 py-1.5 text-left text-sm border focus:outline-none focus:ring-[#1681ba] focus:border-[#1681ba] rounded-md ${
                      activeCount > 0
                        ? 'border-[#1681ba]/40 bg-[#1681ba]/5'
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-1.5">
                      {multiDisplay.mode === 'all' ? (
                        <>
                          <Check className="h-3.5 w-3.5 shrink-0 text-[#1681ba]" aria-hidden />
                          <span className="truncate font-medium text-[#1681ba]">
                            {t('filters.allSelected')}
                          </span>
                          <span className="shrink-0 rounded bg-[#1681ba] px-1.5 py-px text-[10px] font-semibold leading-tight text-white">
                            {baseOptions.length}
                          </span>
                        </>
                      ) : multiDisplay.mode === 'exclusions' ? (
                        <span className="truncate text-gray-800">
                          {t('filters.selectedOfTotal', {
                            selected: selectedValuesForFilter.length,
                            total: baseOptions.length,
                          })}
                        </span>
                      ) : multiDisplay.mode === 'summary' ? (
                        <span className="truncate text-gray-800">
                          {t('filters.selectedCount', { count: multiDisplay.summaryCount })}
                        </span>
                      ) : filter.multiple !== true && activeCount === 1 ? (
                        <span className="truncate font-medium text-gray-800">
                          {localActiveFilters.find((entry) => entry.id === filter.id)?.label}
                        </span>
                      ) : activeCount > 0 ? (
                        <span className="truncate text-gray-800">
                          {t('filters.selectedCount', { count: activeCount })}
                        </span>
                      ) : (
                        <span className="truncate text-gray-500">{t('filters.select')}</span>
                      )}
                    </span>
                    <span className="absolute inset-y-0 right-0 flex items-center pr-1.5">
                      {isOpen ? (
                        <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                      )}
                    </span>
                  </button>

                  {multiDisplay.mode === 'exclusions' &&
                    multiDisplay.excludedChips.length > 0 &&
                    multiDisplay.excludedChips.length <= 5 && (
                    <p
                      className="mt-1 truncate text-[11px] text-amber-800/90"
                      title={multiDisplay.excludedChips.map((chip) => chip.label).join(', ')}
                    >
                      {t('filters.exceptLabel', {
                        names: formatExcludedNames(multiDisplay.excludedChips),
                      })}
                    </p>
                  )}
                  
                  {/* Dropdown content */}
                  {isOpen && (
                    <div className="absolute z-50 mt-0.5 w-full bg-white shadow-lg max-h-52 rounded-md py-0.5 text-xs ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {/* Campo de búsqueda */}
                      <div className="px-2 py-1.5 border-b border-gray-100">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                            <Search className="h-3.5 w-3.5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            placeholder={t('filters.search') || 'Buscar...'}
                            value={searchTerms[filter.id] || ''}
                            onChange={(e) => handleSearchChange(filter.id, e.target.value)}
                            className="block w-full pl-7 pr-7 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-[#1681ba] focus:border-[#1681ba]"
                            onClick={(e) => e.stopPropagation()}
                            autoComplete="off"
                          />
                          {searchTerms[filter.id] && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                clearSearch(filter.id);
                              }}
                              className="absolute inset-y-0 right-0 pr-2 flex items-center"
                            >
                              <X className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Opciones filtradas */}
                      <div 
                        ref={(el) => { scrollContainerRefs.current[filter.id] = el; }}
                        className="overflow-auto max-h-40"
                        onScroll={() => handleScroll(filter.id, baseOptions)}
                      >
                        {useRemote && remoteLoading[filter.id] && filteredOptions.length === 0 ? (
                          <div className="px-3 py-2 text-xs text-gray-500 text-center">
                            {t('filters.loading') || 'Cargando...'}
                          </div>
                        ) : filteredOptions.length === 0 ? (
                          <div className="px-3 py-2 text-xs text-gray-500 text-center">
                            {t('filters.noResults') || 'No se encontraron resultados'}
                          </div>
                        ) : (
                          <>
                            {isFilterSelectAllEnabled(filter) &&
                              allMatchingOptions.length > 0 && (
                              <label className="sticky top-0 z-[1] flex cursor-pointer items-center border-b border-gray-100 bg-gray-50 px-2.5 py-2 text-xs font-medium hover:bg-gray-100">
                                <input
                                  type="checkbox"
                                  checked={allMatchingSelected}
                                  ref={(element) => {
                                    if (!element) return;
                                    const someMatchingSelected =
                                      !allMatchingSelected &&
                                      allMatchingOptions.some((option) =>
                                        selectedValuesForFilter.includes(String(option.value))
                                      );
                                    element.indeterminate = someMatchingSelected;
                                  }}
                                  onChange={() => handleSelectAll(filter.id, baseOptions)}
                                  className="mr-2 h-3.5 w-3.5 rounded border-gray-300 text-[#1681ba] focus:ring-[#1681ba]"
                                />
                                <span className="truncate text-gray-700">
                                  {allMatchingSelected
                                    ? t('filters.deselectAllCount', { count: allMatchingOptions.length })
                                    : t('filters.selectAllCount', { count: allMatchingOptions.length })}
                                </span>
                              </label>
                            )}
                            {filteredOptions.map(option => {
                              const isSelected = selectedValuesForFilter.includes(String(option.value));

                              return (
                                <label
                                  key={String(option.value)}
                                  className="relative flex items-center px-2.5 py-1.5 text-xs hover:bg-gray-100 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleFilterChange(filter.id, String(option.value))}
                                    className="h-3.5 w-3.5 text-[#1681ba] focus:ring-[#1681ba] border-gray-300 rounded mr-2"
                                  />
                                  <span className="block truncate">{option.label}</span>
                                </label>
                              );
                            })}
                            {hasMoreResults && (
                              <div className="px-3 py-1.5 text-[11px] text-gray-500 text-center border-t border-gray-100 bg-gray-50">
                                {t('filters.moreResults') || `Mostrando ${filteredOptions.length} de ${totalFilteredCount} resultados. Desplázate para cargar más.`}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Chips solo para selección manual con pocos ítems */}
                {multiDisplay.mode === 'default' && multiDisplay.includedChips.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {multiDisplay.includedChips.map((activeFilter) => (
                      <span 
                        key={`${filter.id}-${activeFilter.value}`}
                        className="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium bg-blue-100 text-blue-800"
                      >
                        {activeFilter.label}
                        <button
                          type="button"
                          onClick={() => handleFilterChange(filter.id, activeFilter.value)}
                          className="ml-1 inline-flex text-blue-400 focus:outline-none hover:text-blue-600"
                          aria-label={t('filters.clear')}
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {extraContentInline ? extraContent : null}
        </div>
        {!extraContentInline && extraContent ? <div className="mt-3">{extraContent}</div> : null}
        {extraFooter ? <div className="mt-2">{extraFooter}</div> : null}
      </div>
    </div>
  );
}
